import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Paper,
} from '@mui/material';
import {
  Title as TitleIcon,
  Category as CategoryIcon,
  School as SchoolIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';

const BasicInfoForm = ({ data, onChange, mode, editMode = false }) => {
  const { t } = useTranslation();
  const [showOtherCategory, setShowOtherCategory] = useState(data.projectCategory === '其他');
  const [showOtherNature, setShowOtherNature] = useState(data.projectNature === '其他');
  
  // 当data发生变化时，更新状态
  React.useEffect(() => {
    setShowOtherCategory(data.projectCategory === '其他');
    setShowOtherNature(data.projectNature === '其他');
  }, [data.projectCategory, data.projectNature]);

  // 项目类别选项
  const projectCategories = [
    '智能硬件',
    '智慧生活',
    '数字娱乐',
    '通信网络',
    '医疗健康',
    '信息服务',
    '人工智能',
    '教育文化',
    '房产家居',
    '公益创业',
    '理论研究',
    '创意设计',
    '红旅专项',
    '小米专项',
    '其他'
  ];

  // 项目性质选项
  const projectNatures = [
    '硬件',
    '软件',
    '硬件+软件',
    '文化创意',
    '理论研究',
    '其他'
  ];

  // 处理表单变化
  const handleChange = (field, value) => {
    const newData = {
      ...data,
      [field]: value
    };
    onChange(newData);
  };

  // 处理项目类别变化
  const handleCategoryChange = (value) => {
    const newData = {
      ...data,
      projectCategory: value,
      projectCategoryOther: value === '其他' ? data.projectCategoryOther : ''
    };
    onChange(newData);
    setShowOtherCategory(value === '其他');
  };

  // 处理项目性质变化
  const handleNatureChange = (value) => {
    const newData = {
      ...data,
      projectNature: value,
      projectNatureOther: value === '其他' ? data.projectNatureOther : ''
    };
    onChange(newData);
    setShowOtherNature(value === '其他');
  };

  const inputSx = {
    '& .MuiInputLabel-root': { color: '#94a3b8' },
    '& .MuiOutlinedInput-root': {
      color: '#e2e8f0',
      background: 'rgba(255, 255, 255, 0.02)',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
      '&.Mui-focused fieldset': { borderColor: '#38bdf8' }
    },
    '& .MuiInputAdornment-root': { color: '#64748b' }
  };

  const selectSx = {
    color: '#e2e8f0',
    background: 'rgba(255, 255, 255, 0.02)',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#38bdf8' },
    '& .MuiSelect-icon': { color: '#94a3b8' }
  };

  return (
    <Paper 
      sx={{ 
        p: 4, 
        backgroundColor: 'rgba(30, 41, 59, 0.4)', 
        backdropFilter: 'blur(12px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        mb: 3 
      }}
    >
      <Typography variant="h6" sx={{ color: '#e2e8f0', mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <TitleIcon sx={{ color: '#38bdf8' }} />
        {mode === 'proposal' ? t('yuyan_basic_title_proposal') : t('yuyan_basic_title_conclusion')}
      </Typography>
      
      <Grid container spacing={3}>
        {/* 项目名称 */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('yuyan_basic_project_name')}
            value={data.projectName || ''}
            onChange={(e) => handleChange('projectName', e.target.value)}
            required
            disabled={mode === 'conclusion'}
            placeholder={t('yuyan_basic_project_name_placeholder')}
            sx={inputSx}
          />
        </Grid>

        {/* 项目类别 */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: '#94a3b8' }}>{t('yuyan_basic_category')}</InputLabel>
            <Select
              value={data.projectCategory || ''}
              onChange={(e) => handleCategoryChange(e.target.value)}
              disabled={mode === 'conclusion'}
              label={t('yuyan_basic_category')}
              sx={selectSx}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#1e293b',
                    color: '#e2e8f0',
                    '& .MuiMenuItem-root': {
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                      '&.Mui-selected': { bgcolor: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8' }
                    }
                  }
                }
              }}
            >
              {projectCategories.map((category) => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* 其他项目类别输入框 */}
        {showOtherCategory && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('yuyan_basic_category_other')}
              value={data.projectCategoryOther || ''}
              onChange={(e) => handleChange('projectCategoryOther', e.target.value)}
              required
              disabled={mode === 'conclusion'}
              sx={inputSx}
            />
          </Grid>
        )}

        {/* 项目性质 */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: '#94a3b8' }}>{t('yuyan_basic_nature')}</InputLabel>
            <Select
              value={data.projectNature || ''}
              onChange={(e) => handleNatureChange(e.target.value)}
              disabled={mode === 'conclusion'}
              label={t('yuyan_basic_nature')}
              sx={selectSx}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#1e293b',
                    color: '#e2e8f0',
                    '& .MuiMenuItem-root': {
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                      '&.Mui-selected': { bgcolor: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8' }
                    }
                  }
                }
              }}
            >
              {projectNatures.map((nature) => (
                <MenuItem key={nature} value={nature}>{nature}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* 其他项目性质输入框 */}
        {showOtherNature && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('yuyan_basic_nature_other')}
              value={data.projectNatureOther || ''}
              onChange={(e) => handleChange('projectNatureOther', e.target.value)}
              required
              disabled={mode === 'conclusion'}
              sx={inputSx}
            />
          </Grid>
        )}

        {/* 所属学院 */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('yuyan_basic_college')}
            value={data.college || ''}
            onChange={(e) => handleChange('college', e.target.value)}
            required
            disabled={mode === 'conclusion'}
            sx={inputSx}
            InputProps={{
              startAdornment: <SchoolIcon sx={{ mr: 1, color: '#64748b', fontSize: 20 }} />
            }}
          />
        </Grid>

        {/* 项目描述 - 可选填写 */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('yuyan_basic_desc')}
            value={data.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            multiline
            rows={4}
            placeholder={t('yuyan_basic_desc_placeholder')}
            sx={inputSx}
          />
        </Grid>

        {/* 指导教师信息 */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ color: '#94a3b8', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" /> {t('yuyan_basic_supervisor')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('yuyan_basic_supervisor_name')}
            value={data.supervisorName || ''}
            onChange={(e) => handleChange('supervisorName', e.target.value)}
            disabled={mode === 'conclusion'}
            sx={inputSx}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('yuyan_basic_supervisor_contact')}
            value={data.supervisorContact || ''}
            onChange={(e) => handleChange('supervisorContact', e.target.value)}
            disabled={mode === 'conclusion'}
            sx={inputSx}
            InputProps={{
              startAdornment: <PhoneIcon sx={{ mr: 1, color: '#64748b', fontSize: 20 }} />
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default BasicInfoForm;